import { useEffect, useState } from "react";
import { X, Plus } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const SERVICE_TYPES = ["flyer", "logo", "seal", "banner", "web", "other"];
const STATUSES = ["submitted", "preproduction", "design", "review", "final", "delivered"];

/** Modal for admins to manually create a project on behalf of an existing client.
 *  Used for backfilling historical projects (the user has clients but the project
 *  predates the platform's intake form). Skips Stripe + pricing engine. */
export function AdminCreateProjectModal({ open, onClose, onCreated }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(blank());
  const [submitting, setSubmitting] = useState(false);

  function blank() {
    return {
      client_id: "",
      service_type: "logo",
      title: "",
      description: "",
      total_usd: "",
      paid_amount: "",
      status: "delivered",
      payment_status: "",
      created_at: "", // ISO date YYYY-MM-DD; blank = today
      note: "",
    };
  }

  useEffect(() => {
    if (!open) return;
    setForm(blank());
    api.get("/admin/clients").then(({ data }) => setClients(data || [])).catch(() => setClients([]));
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error("Pick a client");
    if (!form.title.trim()) return toast.error("Title is required");
    const total = parseFloat(form.total_usd);
    if (Number.isNaN(total) || total < 0) return toast.error("Total must be ≥ 0");

    const payload = {
      client_id: form.client_id,
      service_type: form.service_type,
      title: form.title.trim(),
      description: form.description || null,
      total_usd: total,
      status: form.status,
    };
    if (form.paid_amount !== "") {
      const paid = parseFloat(form.paid_amount);
      if (Number.isNaN(paid) || paid < 0) return toast.error("Paid amount must be ≥ 0");
      payload.paid_amount = paid;
    }
    if (form.payment_status) payload.payment_status = form.payment_status;
    if (form.created_at) payload.created_at = form.created_at;
    if (form.note.trim()) payload.note = form.note.trim();

    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/projects", payload);
      toast.success(`Created "${data.title}" for ${data.client_name}`);
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create project");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="admin-create-project-modal">
      <div className="bg-[#0a0a0a] border border-[#262626] w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#262626] sticky top-0 bg-[#0a0a0a] z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Backfill</div>
            <div className="font-display text-xl mt-1">Create project for client</div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-create-project-modal"
            className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-5">
          <div className="p-3 border border-[#262626] bg-[#050505] text-xs text-[#a3a3a3] leading-relaxed">
            Use this to <strong className="text-white">add an existing project</strong> for a client you've onboarded.
            Skips Stripe + the pricing wizard — set the totals, status, and date directly.
            No email is sent to the client.
          </div>

          {/* Client */}
          <Field label="Client" required>
            <select
              required
              value={form.client_id}
              onChange={(e) => set("client_id", e.target.value)}
              data-testid="create-project-client"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
            >
              <option value="">— Select a client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.company_name ? `(${c.company_name})` : ""} · {c.email}
                </option>
              ))}
            </select>
          </Field>

          {/* Title + service */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Field label="Project title" required>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  data-testid="create-project-title"
                  placeholder="e.g. Spring revival flyer"
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
                />
              </Field>
            </div>
            <Field label="Service type">
              <select
                value={form.service_type}
                onChange={(e) => set("service_type", e.target.value)}
                data-testid="create-project-service"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none capitalize"
              >
                {SERVICE_TYPES.map((s) => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Description */}
          <Field label="Description (optional)">
            <textarea
              rows={2}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="create-project-description"
              placeholder="Optional — visible to the client on their project page"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none resize-none"
            />
          </Field>

          {/* Financials */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Total (USD)" required>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={form.total_usd}
                onChange={(e) => set("total_usd", e.target.value)}
                data-testid="create-project-total"
                placeholder="0.00"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none tabular-nums"
              />
            </Field>
            <Field label="Paid amount (USD)" hint="Leave blank to auto-set: full total if delivered+paid, else 0">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.paid_amount}
                onChange={(e) => set("paid_amount", e.target.value)}
                data-testid="create-project-paid"
                placeholder="auto"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none tabular-nums"
              />
            </Field>
          </div>

          {/* Status + payment_status + date */}
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                data-testid="create-project-status"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none capitalize"
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s} className="capitalize">{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Payment status" hint="Leave blank to auto-set from status">
              <select
                value={form.payment_status}
                onChange={(e) => set("payment_status", e.target.value)}
                data-testid="create-project-payment-status"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
              >
                <option value="">— auto —</option>
                <option value="unpaid">Unpaid</option>
                <option value="deposit_paid">Deposit paid</option>
                <option value="paid">Paid</option>
                <option value="covered_by_plan">Covered by plan</option>
              </select>
            </Field>
            <Field label="Date (backfill)" hint="Leave blank = today">
              <input
                type="date"
                value={form.created_at}
                onChange={(e) => set("created_at", e.target.value)}
                data-testid="create-project-date"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
              />
            </Field>
          </div>

          {/* Admin note */}
          <Field label="Admin note (optional)" hint="Internal — never shown to the client">
            <input
              type="text"
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
              data-testid="create-project-note"
              placeholder="e.g. Backfilled from invoice #4231"
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none"
            />
          </Field>

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline px-4 py-2 text-xs uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-create-project"
              className="btn-primary px-5 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50"
            >
              <Plus size={12} weight="bold" />
              {submitting ? "Creating…" : "Create project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">
        {label} {required && <span className="text-[#FF2D2D]">*</span>}
      </label>
      {children}
      {hint && <div className="text-[10px] text-[#666] mt-1">{hint}</div>}
    </div>
  );
}
